import React, { useRef, useState } from "react";
import Icon from "../../../components/AppIcon";
import Button from "../../../components/ui/Button";
import Modal from "../../../components/ui/Modal";
import Textarea from "../../../components/ui/Textarea";
import { useToast } from "../../../contexts/ToastContext";
import { Lease } from "../../../store/api/leasesApi";
import {
	TenancyAgreementCreateRequest,
	useCreateTenancyAgreementMutation,
} from "../../../store/api/tenancyAgreementsApi";

interface UploadTenancyAgreementModalProps {
	isOpen: boolean;
	onClose: () => void;
	lease: Lease;
}

const UploadTenancyAgreementModal: React.FC<
	UploadTenancyAgreementModalProps
> = ({ isOpen, onClose, lease }) => {
	const { success, error: showError } = useToast();
	const [createAgreement, { isLoading }] = useCreateTenancyAgreementMutation();
	const [adminNotes, setAdminNotes] = useState("");
	const [selectedFile, setSelectedFile] = useState<File | null>(null);
	const fileInputRef = useRef<HTMLInputElement>(null);

	const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const file = e.target.files?.[0];
		if (file) {
			// Validate file type (PDF preferred)
			if (file.type !== "application/pdf" && !file.name.endsWith(".pdf")) {
				showError("Please upload a PDF file");
				return;
			}
			// Validate file size (max 10MB)
			if (file.size > 10 * 1024 * 1024) {
				showError("File size must be less than 10MB");
				return;
			}
			setSelectedFile(file);
		}
	};

	const convertFileToBase64 = (file: File): Promise<string> => {
		return new Promise((resolve, reject) => {
			const reader = new FileReader();
			reader.readAsDataURL(file);
			reader.onload = () => resolve(reader.result as string);
			reader.onerror = (error) => reject(error);
		});
	};

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();

		if (!selectedFile) {
			showError("Please select a file to upload");
			return;
		}

		try {
			const base64File = await convertFileToBase64(selectedFile);
			const request: TenancyAgreementCreateRequest = {
				lease_id: lease.id,
				document_file: base64File,
				admin_notes: adminNotes || undefined,
			};

			await createAgreement(request).unwrap();
			success("Tenancy agreement uploaded successfully!");
			handleClose();
		} catch (err: any) {
			console.error("Failed to upload agreement:", err);
			showError(
				err?.data?.message || "Failed to upload agreement. Please try again.",
			);
		}
	};

	const handleClose = () => {
		setSelectedFile(null);
		setAdminNotes("");
		if (fileInputRef.current) {
			fileInputRef.current.value = "";
		}
		onClose();
	};

	return (
		<Modal
			isOpen={isOpen}
			onClose={handleClose}
			title="Upload Tenancy Agreement"
			description={`Upload agreement document for lease ${
				lease.lease_code || lease.id
			}`}
		>
			<form onSubmit={handleSubmit} className="space-y-4">
				<div>
					<label className="block text-sm font-medium text-foreground mb-2">
						Agreement Document (PDF)
					</label>
					<div className="flex items-center space-x-4">
						<input
							ref={fileInputRef}
							type="file"
							accept=".pdf,application/pdf"
							onChange={handleFileChange}
							className="hidden"
							id="agreement-file"
						/>
						<label
							htmlFor="agreement-file"
							className="flex items-center space-x-2 px-4 py-2 border border-border rounded-md cursor-pointer hover:bg-muted transition-colors"
						>
							<Icon name="Upload" size={20} />
							<span className="text-sm text-foreground">
								{selectedFile ? selectedFile.name : "Choose File"}
							</span>
						</label>
						{selectedFile && (
							<Button
								type="button"
								variant="ghost"
								size="sm"
								onClick={() => {
									setSelectedFile(null);
									if (fileInputRef.current) {
										fileInputRef.current.value = "";
									}
								}}
							>
								<Icon name="X" size={16} />
							</Button>
						)}
					</div>
					<p className="text-xs text-muted-foreground mt-1">
						Maximum file size: 10MB. PDF format only.
					</p>
				</div>

				<div>
					<label className="block text-sm font-medium text-foreground mb-2">
						Admin Notes (Optional)
					</label>
					<Textarea
						value={adminNotes}
						onChange={(e) => setAdminNotes(e.target.value)}
						placeholder="Add any notes about this agreement..."
						rows={4}
					/>
				</div>

				<div className="flex justify-end space-x-3 pt-4 border-t border-border">
					<Button type="button" variant="outline" onClick={handleClose}>
						Cancel
					</Button>
					<Button type="submit" disabled={!selectedFile || isLoading}>
						{isLoading ? "Uploading..." : "Upload Agreement"}
					</Button>
				</div>
			</form>
		</Modal>
	);
};

export default UploadTenancyAgreementModal;
